import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

const DURATION = 1.2;
const STAGGER = 0.08;

function parseNumberPart(part) {
  if (/^\d{1,3}(\.\d{3})+$/.test(part)) {
    return parseInt(part.replace(/\./g, ""), 10);
  }

  if (/^\d{1,3}(\.\d{3})+,\d+$/.test(part)) {
    return parseFloat(part.replace(/\./g, "").replace(",", "."));
  }

  if (part.includes(",")) {
    const afterComma = part.split(",")[1] ?? "";

    if (afterComma.length === 3 && !part.includes(".")) {
      return parseInt(part.replace(/,/g, ""), 10);
    }

    return parseFloat(part.replace(",", "."));
  }

  return parseFloat(part);
}

function parseFactsValue(raw) {
  const text = String(raw).trim();
  const match = text.match(/^([^\d]*?)([\d][\d.,]*)(.*)$/);

  if (!match) {
    return null;
  }

  const [, prefix, numberPart, suffix] = match;
  const num = parseNumberPart(numberPart);

  if (!Number.isFinite(num)) {
    return null;
  }

  return { prefix, suffix, num, numberPart };
}

function formatFactsValue(value, parsed) {
  const { prefix, suffix, numberPart } = parsed;
  let formatted;

  if (/^\d{1,3}(\.\d{3})+$/.test(numberPart)) {
    formatted = Math.round(value).toLocaleString("de-DE");
  } else if (/^\d{1,3}(\.\d{3})+,\d+$/.test(numberPart)) {
    const decimals = (numberPart.split(",")[1] ?? "").length;
    formatted = value.toFixed(decimals).replace(".", ",");
  } else if (numberPart.includes(",")) {
    const afterComma = numberPart.split(",")[1] ?? "";

    if (afterComma.length === 3 && !numberPart.includes(".")) {
      formatted = Math.round(value).toLocaleString("de-DE");
    } else {
      const decimals = afterComma.length;
      formatted = value.toFixed(decimals).replace(".", ",");
    }
  } else if (numberPart.includes(".")) {
    const decimals = (numberPart.split(".")[1] ?? "").length;
    formatted = value.toFixed(decimals);
  } else {
    formatted = String(Math.round(value));
  }

  return `${prefix}${formatted}${suffix}`;
}

function tweenFactsValue(element, parsed, delay = 0) {
  const proxy = { v: 0 };

  return gsap.to(proxy, {
    v: parsed.num,
    duration: DURATION,
    delay,
    ease: "power1.out",
    onUpdate: () => {
      element.textContent = formatFactsValue(proxy.v, parsed);
    },
  });
}

function initFactsSection(section) {
  const values = [...section.querySelectorAll(".facts__value[data-facts-value]")];
  const items = values
    .map((element) => ({
      element,
      parsed: parseFactsValue(element.dataset.factsValue),
    }))
    .filter((item) => item.parsed);

  if (!items.length) {
    return;
  }

  if (reducedMotion) {
    return;
  }

  items.forEach(({ element, parsed }) => {
    element.textContent = formatFactsValue(0, parsed);
  });

  ScrollTrigger.create({
    trigger: section,
    start: "top 70%",
    once: true,
    onEnter: () => {
      items.forEach(({ element, parsed }, index) => {
        tweenFactsValue(element, parsed, index * STAGGER);
      });
    },
  });
}

document.querySelectorAll(".facts").forEach(initFactsSection);

window.addEventListener(
  "load",
  () => {
    ScrollTrigger.refresh();
  },
  { once: true },
);
